import type {
  OntologyEntity,
  OntologyJoinPath,
  OntologyMetric,
  OntologySemanticMapping,
  StructuredAssetAllowlistRow,
  WorkspaceOntology,
} from './structuredAnalyticsConfig';

export interface OntologyGraphMetric {
  id: string;
  name: string;
  expression: string;
  description: string;
}

export interface OntologyGraphBusinessMeaning {
  id: string;
  label: string;
  conceptKey: string;
  valueKind?: string | null;
  defaultAggregation?: string | null;
}

export interface OntologyGraphNode {
  id: string;
  entityId: string;
  entity: OntologyEntity;
  label: string;
  description: string;
  assetLabel: string;
  catalogBadge: StructuredAssetAllowlistRow['catalogBadge'] | 'unmapped';
  catalogLinkStatus: OntologyCatalogLinkStatus;
  metrics: OntologyGraphMetric[];
  businessMeanings: OntologyGraphBusinessMeaning[];
  synonymCount: number;
  bindingCount: number;
  semanticMappingCount: number;
  relationshipCount: number;
  groupedEntityCount?: number;
  groupedEntityLabels?: string[];
  position: { x: number; y: number };
}

export interface OntologyGraphEdge {
  id: string;
  joinId: string;
  join: OntologyJoinPath;
  source: string;
  target: string;
  sourceLabel: string;
  targetLabel: string;
  label: string;
  routeLabel: string;
  joinCondition: string;
  joinEvidenceLabel: string;
  joinEvidenceStatus: 'validated' | 'draft' | 'failed' | 'legacy_sql' | 'missing';
  joinEvidenceStatusLabel: string;
  relationshipKind: string;
  traversalAllowed: boolean;
  cardinalityLabel: string;
  reviewStatusLabel: string;
  reviewStatusTone: 'neutral' | 'warning' | 'danger' | 'success';
  description: string;
}

export interface OntologyGraphModel {
  nodes: OntologyGraphNode[];
  edges: OntologyGraphEdge[];
  danglingJoins: OntologyJoinPath[];
  summary: {
    entities: number;
    metrics: number;
    semanticMappings: number;
    joins: number;
  };
}

export interface OntologyGraphProjection {
  nodes: OntologyGraphNode[];
  edges: OntologyGraphEdge[];
}

export type OntologyGraphDensity = 'overview' | 'condensed' | 'comfortable';

const NODE_GAPS: Record<OntologyGraphDensity, { x: number; y: number }> = {
  overview: { x: 180, y: 120 },
  comfortable: { x: 370, y: 480 },
  condensed: { x: 230, y: 150 },
};

export type OntologyCatalogLinkStatus = 'linked' | 'partial' | 'unlinked' | 'invalid';

export function resolveOntologyEntityAsset(
  entity: OntologyEntity,
  allowlist: StructuredAssetAllowlistRow[],
): StructuredAssetAllowlistRow | null {
  if (!entity.physicalTable) return null;
  const ref = entity.physicalTable.startsWith('table:')
    ? entity.physicalTable.slice('table:'.length)
    : entity.physicalTable;

  return (
    allowlist.find((row) => row.id === ref) ??
    allowlist.find((row) => `${row.dataset}.${row.objectName}` === ref) ??
    null
  );
}

export function getOntologyCatalogLinkStatus(
  entity: OntologyEntity,
  allowlist: StructuredAssetAllowlistRow[],
): OntologyCatalogLinkStatus {
  if (!entity.physicalTable) return 'unlinked';
  const asset = resolveOntologyEntityAsset(entity, allowlist);
  if (!asset) return 'invalid';
  if (asset.catalogBadge === 'matched') return 'linked';
  if (asset.catalogBadge === 'ambiguous' || asset.catalogBadge === 'stale') return 'partial';
  return 'unlinked';
}

export function ontologyCatalogLinkStatusLabel(status: OntologyCatalogLinkStatus): string {
  switch (status) {
    case 'linked':
      return 'Linked';
    case 'partial':
      return 'Needs review';
    case 'invalid':
      return 'Invalid link';
    case 'unlinked':
      return 'Unlinked';
    default:
      return status;
  }
}

function assetLabel(entity: OntologyEntity, allowlist: StructuredAssetAllowlistRow[]): string {
  const asset = resolveOntologyEntityAsset(entity, allowlist);
  if (asset) return `${asset.dataset}.${asset.objectName}`;
  return entity.physicalTable || 'No asset mapped';
}

function sourceGroupLabel(value: string): string {
  const sourceName = value.split('.').pop() || value;
  return sourceName
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

/**
 * The overview is a source-level map, not a wall of every logical facet.
 * Several logical entities may legitimately describe one physical source, but
 * showing all of them as near-identical cards makes the topology unreadable.
 * Entity and detail levels retain the individual concepts.
 */
export function buildOntologyOverviewProjection(
  nodes: OntologyGraphNode[],
  edges: OntologyGraphEdge[],
): OntologyGraphProjection {
  const groups = new Map<string, OntologyGraphNode[]>();
  nodes.forEach((node) => {
    const hasPhysicalSource = node.assetLabel && node.assetLabel !== 'No asset mapped';
    const groupKey = hasPhysicalSource ? `source:${node.assetLabel}` : node.id;
    groups.set(groupKey, [...(groups.get(groupKey) ?? []), node]);
  });

  const nodeIdByEntityNodeId = new Map<string, string>();
  const overviewNodes = Array.from(groups.entries()).map(([groupKey, members]) => {
    const representative = [...members].sort((left, right) =>
      left.label.localeCompare(right.label),
    )[0];
    const overviewId = members.length > 1 ? groupKey : representative.id;
    members.forEach((member) => nodeIdByEntityNodeId.set(member.id, overviewId));
    return {
      ...representative,
      id: overviewId,
      label:
        members.length > 1
          ? sourceGroupLabel(representative.assetLabel)
          : representative.label,
      bindingCount: members.reduce((count, member) => count + member.bindingCount, 0),
      semanticMappingCount: members.reduce(
        (count, member) => count + member.semanticMappingCount,
        0,
      ),
      relationshipCount: members.reduce(
        (count, member) => count + member.relationshipCount,
        0,
      ),
      groupedEntityCount: members.length,
      groupedEntityLabels: members.map((member) => member.label).sort(),
    };
  });

  const overviewNodeById = new Map(overviewNodes.map((node) => [node.id, node]));
  const seenPairs = new Set<string>();
  const overviewEdges = edges.flatMap((edge) => {
    const source = nodeIdByEntityNodeId.get(edge.source);
    const target = nodeIdByEntityNodeId.get(edge.target);
    if (!source || !target || source === target) return [];
    const pairKey = [source, target].sort().join('::');
    if (seenPairs.has(pairKey)) return [];
    seenPairs.add(pairKey);
    const sourceLabel = overviewNodeById.get(source)?.label ?? edge.sourceLabel;
    const targetLabel = overviewNodeById.get(target)?.label ?? edge.targetLabel;
    return [{
      ...edge,
      id: `overview:${pairKey}`,
      source,
      target,
      sourceLabel,
      targetLabel,
      routeLabel: `${sourceLabel} -> ${targetLabel}`,
    }];
  });

  return { nodes: overviewNodes, edges: overviewEdges };
}

function countRelationships(entityId: string, joins: OntologyJoinPath[]): number {
  return joins.filter((join) => join.leftEntityId === entityId || join.rightEntityId === entityId).length;
}

function countDirectSourceBindings(entity: OntologyEntity): number {
  if (!entity.sourceBindings) return entity.physicalTable ? 1 : 0;
  return entity.sourceBindings.filter((binding) => binding.executionMode !== 'semantic_only').length;
}

function humanizeRelationshipName(value: string): string {
  const smallWords = new Set(['and', 'as', 'by', 'for', 'from', 'in', 'into', 'of', 'on', 'or', 'to', 'with']);
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((word, index) => {
      const lower = word.toLowerCase();
      if (index > 0 && smallWords.has(lower)) return lower;
      return lower.replace(/^\w/, (character) => character.toUpperCase());
    })
    .join(' ');
}

function compactText(value: string | null | undefined): string | null {
  const compacted = value?.replace(/\s+/g, ' ').trim();
  return compacted || null;
}

function looksLikeSql(value: string): boolean {
  return /[.=()]/.test(value) || /\b(join|select|where| from | on )\b/i.test(value);
}

function relationshipLabel(
  join: OntologyJoinPath,
  sourceEntity?: OntologyEntity,
  targetEntity?: OntologyEntity,
): string {
  const predicateLabel = compactText(join.predicate);
  if (predicateLabel && predicateLabel.length <= 48 && !looksLikeSql(predicateLabel)) {
    return humanizeRelationshipName(predicateLabel);
  }
  const explicitLabel = compactText(join.name);
  if (explicitLabel && explicitLabel.length <= 48 && !looksLikeSql(explicitLabel)) {
    return humanizeRelationshipName(explicitLabel);
  }
  const descriptionLabel = compactText(join.description);
  if (descriptionLabel && descriptionLabel.length <= 48 && !looksLikeSql(descriptionLabel)) {
    return humanizeRelationshipName(descriptionLabel);
  }
  if (explicitLabel && explicitLabel.length <= 48) {
    return explicitLabel;
  }
  return `${sourceEntity?.name ?? 'Entity'} relates to ${targetEntity?.name ?? 'Entity'}`;
}

function joinEvidenceLabel(join: OntologyJoinPath): string {
  const condition = compactText(join.joinCondition);
  if (!condition && relationshipKind(join) === 'semantic_only') return 'Semantic only';
  return condition || 'Join evidence needed';
}

function joinEvidenceStatus(join: OntologyJoinPath): OntologyGraphEdge['joinEvidenceStatus'] {
  const status = compactText(join.joinEvidenceStatus)?.toLowerCase();
  if (status === 'validated' || status === 'draft' || status === 'failed' || status === 'legacy_sql') {
    return status;
  }
  if (Array.isArray(join.joinConditions) && join.joinConditions.length > 0) {
    const statuses = new Set(
      join.joinConditions
        .map((condition) => condition.validation_status ? String(condition.validation_status).toLowerCase() : '')
        .filter(Boolean),
    );
    if (statuses.size > 0 && Array.from(statuses).every((item) => item === 'validated')) return 'validated';
    if (statuses.has('failed')) return 'failed';
    if (statuses.size === 0 && compactText(join.joinCondition)) return 'legacy_sql';
    return 'draft';
  }
  if (compactText(join.joinCondition)) return 'legacy_sql';
  return 'missing';
}

function joinEvidenceStatusLabel(join: OntologyJoinPath): string {
  const status = joinEvidenceStatus(join);
  if (status === 'validated') return 'Validated evidence';
  if (status === 'draft') return 'Unvalidated evidence';
  if (status === 'failed') return 'Failed evidence';
  if (status === 'legacy_sql') return 'Legacy SQL';
  return 'Missing evidence';
}

function relationshipKind(join: OntologyJoinPath): string {
  return compactText(join.relationshipKind) ?? (compactText(join.joinCondition) ? 'join_backed' : 'semantic_only');
}

function traversalAllowed(join: OntologyJoinPath): boolean {
  const evidenceStatus = joinEvidenceStatus(join);
  return (
    relationshipKind(join) === 'join_backed'
    && evidenceStatus === 'validated'
    && join.traversalAllowed !== false
  );
}

function cardinalityLabel(join: OntologyJoinPath): string {
  const cardinality = compactText(join.cardinality);
  return cardinality ? humanizeRelationshipName(cardinality) : 'Cardinality needed';
}

function reviewStatusLabel(join: OntologyJoinPath): string {
  const status = compactText(join.status)?.toLowerCase();
  const draftLikeStatus = status === 'draft' || status === 'pending' || status === 'needs_review';
  if (!status || draftLikeStatus) {
    if (traversalAllowed(join)) return 'SQL traversable';
    if (relationshipKind(join) === 'semantic_only') return 'Semantic only';
    const evidenceStatus = joinEvidenceStatus(join);
    if (evidenceStatus === 'draft') return 'Needs validation';
    if (evidenceStatus === 'failed') return 'Evidence failed';
    if (evidenceStatus === 'missing') return 'Missing evidence';
    return 'Not SQL ready';
  }
  if (relationshipKind(join) === 'semantic_only') return 'Semantic only';
  if (!traversalAllowed(join)) {
    const evidenceStatus = joinEvidenceStatus(join);
    if (evidenceStatus === 'draft') return 'Needs validation';
    if (evidenceStatus === 'failed') return 'Evidence failed';
    if (evidenceStatus === 'missing') return 'Missing evidence';
    return 'Not SQL ready';
  }
  return humanizeRelationshipName(status);
}

function reviewStatusTone(join: OntologyJoinPath): OntologyGraphEdge['reviewStatusTone'] {
  const status = compactText(join.status)?.toLowerCase();
  if (status === 'draft' || status === 'pending' || status === 'needs_review') {
    if (relationshipKind(join) === 'semantic_only') return 'neutral';
    return traversalAllowed(join) ? 'success' : 'warning';
  }
  if (status === 'approved' || status === 'linked' || status === 'valid') return 'success';
  if (status === 'invalid' || status === 'failed' || status === 'blocked') return 'danger';
  if (relationshipKind(join) === 'semantic_only') return 'neutral';
  return traversalAllowed(join) ? 'success' : 'warning';
}

export function layoutOntologyGraphPosition(
  index: number,
  total: number,
  density: OntologyGraphDensity = 'comfortable',
): { x: number; y: number } {
  const columns = Math.max(1, Math.ceil(Math.sqrt(total)));
  const gap = NODE_GAPS[density];
  return {
    x: (index % columns) * gap.x,
    y: Math.floor(index / columns) * gap.y,
  };
}

/**
 * Produces a deterministic, relationship-aware layout without requiring a
 * browser-only layout engine. Each connected component is arranged from its
 * most connected entity outwards, so related entities form readable lanes
 * instead of one alphabetically ordered rectangle.
 */
export function layoutOntologyGraph(
  nodes: Pick<OntologyGraphNode, 'id' | 'label'>[],
  edges: Pick<OntologyGraphEdge, 'source' | 'target'>[],
  density: OntologyGraphDensity = 'comfortable',
): Record<string, { x: number; y: number }> {
  if (nodes.length === 0) return {};

  const gap = NODE_GAPS[density];
  const maxRows = density === 'overview' ? 9 : density === 'condensed' ? 7 : 5;
  const nodeIds = new Set(nodes.map((node) => node.id));
  const labels = new Map(nodes.map((node) => [node.id, node.label]));
  const adjacency = new Map(nodes.map((node) => [node.id, new Set<string>()]));

  edges.forEach((edge) => {
    if (!nodeIds.has(edge.source) || !nodeIds.has(edge.target) || edge.source === edge.target) return;
    adjacency.get(edge.source)?.add(edge.target);
    adjacency.get(edge.target)?.add(edge.source);
  });

  const components: string[][] = [];
  const visited = new Set<string>();
  const orderedIds = nodes
    .map((node) => node.id)
    .sort((left, right) => compareNodeIds(left, right, adjacency, labels));

  orderedIds.forEach((startId) => {
    if (visited.has(startId)) return;
    const component: string[] = [];
    const queue = [startId];
    visited.add(startId);
    while (queue.length > 0) {
      const nodeId = queue.shift()!;
      component.push(nodeId);
      const neighbours = Array.from(adjacency.get(nodeId) ?? [])
        .sort((left, right) => compareNodeIds(left, right, adjacency, labels));
      neighbours.forEach((neighbourId) => {
        if (visited.has(neighbourId)) return;
        visited.add(neighbourId);
        queue.push(neighbourId);
      });
    }
    components.push(component);
  });

  components.sort((left, right) => {
    if (right.length !== left.length) return right.length - left.length;
    return compareNodeIds(left[0], right[0], adjacency, labels);
  });

  const componentLayouts = components.map((component) => {
    const root = [...component].sort(
      (left, right) => compareNodeIds(left, right, adjacency, labels),
    )[0];
    const layers = buildRelationshipLayers(root, component, adjacency, labels);
    const columns = layers.flatMap((layer) => chunk(layer, maxRows));
    const componentHeight = Math.max(
      gap.y,
      ...columns.map((columnNodes) => columnNodes.length * gap.y),
    );
    const localPositions: Record<string, { x: number; y: number }> = {};

    columns.forEach((columnNodes, column) => {
      const columnHeight = Math.max(gap.y, columnNodes.length * gap.y);
      const top = (componentHeight - columnHeight) / 2;
      columnNodes.forEach((nodeId, row) => {
        localPositions[nodeId] = {
          x: column * gap.x,
          y: top + row * gap.y,
        };
      });
    });

    return {
      positions: localPositions,
      width: Math.max(gap.x, columns.length * gap.x),
      height: componentHeight,
    };
  });

  const componentGapX = gap.x * 0.35;
  const componentGapY = gap.y * 0.55;
  const totalArea = componentLayouts.reduce(
    (area, component) =>
      area
      + (component.width + componentGapX)
      * (component.height + componentGapY),
    0,
  );
  const targetWidth = Math.max(
    gap.x * 4,
    Math.ceil(Math.sqrt(totalArea * 1.5) / gap.x) * gap.x,
  );
  const positions: Record<string, { x: number; y: number }> = {};
  let cursorX = 0;
  let cursorY = 0;
  let rowHeight = 0;

  componentLayouts.forEach((component) => {
    if (cursorX > 0 && cursorX + component.width > targetWidth) {
      cursorX = 0;
      cursorY += rowHeight + componentGapY;
      rowHeight = 0;
    }
    Object.entries(component.positions).forEach(([nodeId, position]) => {
      positions[nodeId] = {
        x: cursorX + position.x,
        y: cursorY + position.y,
      };
    });
    cursorX += component.width + componentGapX;
    rowHeight = Math.max(rowHeight, component.height);
  });

  return positions;
}

function buildRelationshipLayers(
  root: string,
  component: string[],
  adjacency: Map<string, Set<string>>,
  labels: Map<string, string>,
): string[][] {
  const depth = new Map<string, number>([[root, 0]]);
  const queue = [root];
  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    const nextDepth = (depth.get(nodeId) ?? 0) + 1;
    Array.from(adjacency.get(nodeId) ?? [])
      .sort((left, right) => compareNodeIds(left, right, adjacency, labels))
      .forEach((neighbourId) => {
        if (depth.has(neighbourId)) return;
        depth.set(neighbourId, nextDepth);
        queue.push(neighbourId);
      });
  }

  const layers: string[][] = [];
  component.forEach((nodeId) => {
    const nodeDepth = depth.get(nodeId) ?? 0;
    if (!layers[nodeDepth]) layers[nodeDepth] = [];
    layers[nodeDepth].push(nodeId);
  });

  const previousOrder = new Map<string, number>();
  layers.forEach((layer, layerIndex) => {
    layer.sort((left, right) => {
      if (layerIndex > 0) {
        const leftBarycenter = neighbourBarycenter(left, previousOrder, adjacency);
        const rightBarycenter = neighbourBarycenter(right, previousOrder, adjacency);
        if (leftBarycenter !== rightBarycenter) return leftBarycenter - rightBarycenter;
      }
      return compareNodeIds(left, right, adjacency, labels);
    });
    layer.forEach((nodeId, index) => previousOrder.set(nodeId, index));
  });
  return layers;
}

function neighbourBarycenter(
  nodeId: string,
  previousOrder: Map<string, number>,
  adjacency: Map<string, Set<string>>,
): number {
  const positions = Array.from(adjacency.get(nodeId) ?? [])
    .map((neighbourId) => previousOrder.get(neighbourId))
    .filter((value): value is number => value !== undefined);
  if (positions.length === 0) return Number.MAX_SAFE_INTEGER;
  return positions.reduce((sum, value) => sum + value, 0) / positions.length;
}

function compareNodeIds(
  left: string,
  right: string,
  adjacency: Map<string, Set<string>>,
  labels: Map<string, string>,
): number {
  const degreeDifference = (adjacency.get(right)?.size ?? 0) - (adjacency.get(left)?.size ?? 0);
  if (degreeDifference !== 0) return degreeDifference;
  return (labels.get(left) ?? left).localeCompare(labels.get(right) ?? right);
}

function chunk<T>(items: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    result.push(items.slice(index, index + size));
  }
  return result;
}

export function buildOntologyGraphModel(
  ontology: WorkspaceOntology,
  allowlist: StructuredAssetAllowlistRow[],
): OntologyGraphModel {
  const entityIds = new Set(ontology.entities.map((entity) => entity.id));
  const visibleEntities = ontology.entities;
  const visibleEntityIds = new Set(visibleEntities.map((entity) => entity.id));
  const metricsByEntity = new Map<string, OntologyMetric[]>();
  const mappingsByEntity = new Map<string, OntologySemanticMapping[]>();

  ontology.metrics.forEach((metric) => {
    if (!visibleEntityIds.has(metric.entityId)) return;
    const existing = metricsByEntity.get(metric.entityId) ?? [];
    existing.push(metric);
    metricsByEntity.set(metric.entityId, existing);
  });
  (ontology.semanticMappings ?? []).forEach((mapping) => {
    if (!visibleEntityIds.has(mapping.entityId)) return;
    const existing = mappingsByEntity.get(mapping.entityId) ?? [];
    existing.push(mapping);
    mappingsByEntity.set(mapping.entityId, existing);
  });

  const nodes: OntologyGraphNode[] = visibleEntities.map((entity, index) => {
    const asset = resolveOntologyEntityAsset(entity, allowlist);
    return {
      id: `entity-${entity.id}`,
      entityId: entity.id,
      entity,
      label: entity.name,
      description: entity.description,
      assetLabel: assetLabel(entity, allowlist),
      catalogBadge: asset?.catalogBadge ?? 'unmapped',
      catalogLinkStatus: getOntologyCatalogLinkStatus(entity, allowlist),
      metrics: (metricsByEntity.get(entity.id) ?? []).map((metric) => ({
        id: metric.id,
        name: metric.name,
        expression: metric.expression,
        description: metric.description,
      })),
      businessMeanings: (mappingsByEntity.get(entity.id) ?? []).map((mapping) => ({
        id: mapping.id,
        label: mapping.label,
        conceptKey: mapping.conceptKey,
        valueKind: mapping.valueKind,
        defaultAggregation: mapping.defaultAggregation,
      })),
      synonymCount: entity.synonyms.length,
      bindingCount: countDirectSourceBindings(entity),
      relationshipCount: countRelationships(entity.id, ontology.joins),
      semanticMappingCount: mappingsByEntity.get(entity.id)?.length ?? 0,
      position: layoutOntologyGraphPosition(index, visibleEntities.length),
    };
  });

  const edges: OntologyGraphEdge[] = [];
  const danglingJoins: OntologyJoinPath[] = [];

  ontology.joins.forEach((join) => {
    if (!entityIds.has(join.leftEntityId) || !entityIds.has(join.rightEntityId)) {
      danglingJoins.push(join);
      return;
    }
    if (!visibleEntityIds.has(join.leftEntityId) || !visibleEntityIds.has(join.rightEntityId)) {
      return;
    }
    const sourceEntity = ontology.entities.find((entity) => entity.id === join.leftEntityId);
    const targetEntity = ontology.entities.find((entity) => entity.id === join.rightEntityId);
    edges.push({
      id: `join-${join.id}`,
      joinId: join.id,
      join,
      source: `entity-${join.leftEntityId}`,
      target: `entity-${join.rightEntityId}`,
      sourceLabel: sourceEntity?.name ?? 'Entity',
      targetLabel: targetEntity?.name ?? 'Entity',
      label: relationshipLabel(join, sourceEntity, targetEntity),
      routeLabel: `${sourceEntity?.name ?? 'Entity'} -> ${targetEntity?.name ?? 'Entity'}`,
      joinCondition: join.joinCondition,
      joinEvidenceLabel: joinEvidenceLabel(join),
      joinEvidenceStatus: joinEvidenceStatus(join),
      joinEvidenceStatusLabel: joinEvidenceStatusLabel(join),
      relationshipKind: relationshipKind(join),
      traversalAllowed: traversalAllowed(join),
      cardinalityLabel: cardinalityLabel(join),
      reviewStatusLabel: reviewStatusLabel(join),
      reviewStatusTone: reviewStatusTone(join),
      description: join.description,
    });
  });

  return {
    nodes,
    edges,
    danglingJoins,
    summary: {
      entities: visibleEntities.length,
      metrics: Array.from(metricsByEntity.values()).reduce((count, metrics) => count + metrics.length, 0),
      semanticMappings: (ontology.semanticMappings ?? []).filter((mapping) =>
        visibleEntityIds.has(mapping.entityId),
      ).length,
      joins: edges.length,
    },
  };
}
